import { Button as UiButton, Sheet } from "./ui";
import { version as appVersion } from "../package.json";
import {
  Checkbox,
  NativeSelect,
  Notification,
  Tabs,
  Switch,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useDebouncedValue, useMediaQuery } from "@mantine/hooks";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  lazy,
  Suspense,
} from "react";
import {
  ArrowUpRight,
  Bell,
  CalendarDays,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  Clock3,
  Copy,
  ExternalLink,
  Filter,
  FileText,
  FolderKanban,
  GitMerge,
  GitPullRequest,
  Layers3,
  Link2,
  List,
  LoaderCircle,
  MoreHorizontal,
  MessageCircle,
  Plus,
  Radio,
  RefreshCw,
  Search,
  Send,
  Settings2,
  SlidersHorizontal,
  Sparkles,
  Star,
  Users,
  X,
  Zap,
  AlertCircle,
} from "lucide-react";
import type {
  Activity,
  Discipline,
  Bootstrap,
  Priority,
  Project,
  ProjectData,
  RepositoryChoice,
  Status,
  Task,
  User,
  Member,
  TaskSummary,
} from "../shared/types";
import {
  disciplines,
  disciplineNames,
  statuses,
  statusNames,
} from "../shared/types";
import { api, ApiError, patch, post } from "./api";
import { assigneeName } from "./AssigneePicker";
import { taskPriorityNames } from "./TaskFields";
import TaskEditor from "./TaskEditor";
import TaskList from "./TaskList";
import MemberTeamFilter from "./MemberTeamFilter";
import { useTelegramBackButton, useTelegramEnvironment } from "./telegram";
import { miniAppProjectTarget, miniAppTaskTarget } from "../shared/telegram";
const ProjectsView = lazy(() => import("./ProjectsView"));
const AiPanel = lazy(() => import("./AiPanel"));
const ProjectManager = lazy(() => import("./ProjectManager"));
const ProjectPicker = lazy(() => import("./ProjectPicker"));
const ProjectDocuments = lazy(() => import("./ProjectDocuments"));

function Github({
  size = 20,
  className = "",
}: {
  size?: number;
  className?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="none"
      className={className}
      aria-hidden="true"
    >
      <path d="M12 .8a11.2 11.2 0 0 0-3.54 21.83c.56.1.76-.24.76-.54v-2.08c-3.11.67-3.77-1.33-3.77-1.33-.51-1.3-1.24-1.65-1.24-1.65-1.02-.7.08-.68.08-.68 1.12.08 1.71 1.15 1.71 1.15 1 1.7 2.62 1.21 3.26.93.1-.72.39-1.21.71-1.49-2.48-.28-5.1-1.24-5.1-5.54 0-1.22.44-2.22 1.15-3-.12-.28-.5-1.42.11-2.96 0 0 .94-.3 3.08 1.15a10.75 10.75 0 0 1 5.6 0c2.14-1.45 3.08-1.15 3.08-1.15.61 1.54.23 2.68.11 2.96.71.78 1.15 1.78 1.15 3 0 4.31-2.62 5.25-5.12 5.53.4.35.76 1.03.76 2.07v3.09c0 .3.2.65.77.54A11.2 11.2 0 0 0 12 .8Z" />
    </svg>
  );
}
const names = statusNames;
const priorities = taskPriorityNames;
const emptyData: ProjectData = {
  tasks: [],
  assignee_options: [],
  members: [],
  activities: [],
  jobs: [],
  pagination: {
    page: 1,
    page_size: 50,
    total: 0,
    pages: 1,
    status_counts: {
      todo: 0,
      in_progress: 0,
      in_review: 0,
      done: 0,
      cancelled: 0,
    },
  },
  summary: { total: 0, done: 0, mine: 0, review: 0, issue_types: [] },
};
type View =
  "projects" | "board" | "docs" | "members" | "ai" | "activity" | "settings";
function relative(date: string) {
  const minutes = Math.max(
    0,
    Math.floor((Date.now() - new Date(date).valueOf()) / 60000),
  );
  return minutes < 1
    ? "刚刚"
    : minutes < 60
      ? `${minutes} 分钟前`
      : minutes < 1440
        ? `${Math.floor(minutes / 60)} 小时前`
        : `${Math.floor(minutes / 1440)} 天前`;
}
function Avatar({ user, size = "normal" }: { user?: User; size?: string }) {
  const colors = ["blue", "purple", "orange", "green"];
  const index = user
    ? user.id.split("").reduce((n, c) => n + c.charCodeAt(0), 0) % 4
    : 0;
  return (
    <span
      title={user?.name || "未分配"}
      className={`avatar ${colors[index]} ${size}`}
    >
      {user?.name.slice(-2) || "?"}
    </span>
  );
}
function safeUrl(value: string | null) {
  if (!value) return undefined;
  try {
    const u = new URL(value);
    return u.protocol === "https:" ? u.toString() : undefined;
  } catch {
    return undefined;
  }
}

const Overlay = Sheet;

export function App() {
  useTelegramEnvironment();
  const [boot, setBoot] = useState<Bootstrap | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [authError, setAuthError] = useState("");
  const [projectId, setProjectId] = useState("");
  const [data, setData] = useState<ProjectData>(emptyData);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>(() => {
    const requested = new URLSearchParams(location.search).get("view");
    if (requested === "tasks") return "board";
    return requested &&
      ["ai", "settings", "members", "activity", "projects", "docs"].includes(
        requested,
      )
      ? (requested as View)
      : "projects";
  });
  const mobile = useMediaQuery("(max-width: 700px)");
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 250);
  const [pageState, setPageState] = useState({ key: "", page: 1 });
  const taskPages = useRef<{ key: string; pages: Map<number, TaskSummary[]> }>({
    key: "",
    pages: new Map(),
  });
  const loadedProject = useRef("");
  const [assignee, setAssignee] = useState("");
  const [priority, setPriority] = useState("");
  const [status, setStatus] = useState("");
  const [issueTypeFilter, setIssueTypeFilter] = useState("");
  const [filters, setFilters] = useState(false);
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const secondaryFrom = useRef<"projects" | "board" | "docs" | "activity">(
    "board",
  );
  const documentNavigation = useRef<(() => boolean) | null>(null);
  const setDocumentGuard = useCallback((guard: (() => boolean) | null) => {
    documentNavigation.current = guard;
  }, []);
  const [onlyMine, setOnlyMine] = useState(false);
  const [newTask, setNewTask] = useState<Status | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [toast, setToast] = useState("");
  const [modal, setModal] = useState<
    | "invite"
    | "project"
    | "repositories"
    | "manage"
    | "switch"
    | "member-team"
    | null
  >(null);
  const [invite, setInvite] = useState("");
  const [repositories, setRepositories] = useState<RepositoryChoice[]>([]);
  const [busy, setBusy] = useState(false);
  const [savingFavorites, setSavingFavorites] = useState<Set<string>>(
    new Set(),
  );
  const openedProject = useRef("");
  const openingWrites = useRef<Promise<unknown>>(Promise.resolve());
  const searchRef = useRef<HTMLInputElement>(null);
  const startedRef = useRef(false);
  const workspaceVersion = useRef(0);
  const preferenceVersion = useRef(0);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notify = useCallback((text: string) => {
    setToast(text);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 4500);
  }, []);
  const fail = useCallback(
    (error: unknown) =>
      notify(error instanceof Error ? error.message : "操作失败，请稍后重试"),
    [notify],
  );
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void (async () => {
      try {
        const tg = window.Telegram?.WebApp;
        tg?.ready();
        if (tg?.initData)
          await post("/api/auth/telegram", { initData: tg.initData });
        const initial = await api<Bootstrap>("/api/bootstrap");
        setBoot(initial);
        const start = tg?.initDataUnsafe?.start_param;
        const code =
          new URLSearchParams(location.search).get("invite") ||
          (start?.startsWith("invite_") ? start.slice(7) : "");
        const lastProject = [...initial.projects]
          .filter((p) => p.last_opened_at)
          .sort(
            (a, b) =>
              new Date(b.last_opened_at!).valueOf() -
              new Date(a.last_opened_at!).valueOf(),
          )[0];
        let id =
          lastProject?.id ||
          initial.projects.find((p) => p.favorite)?.id ||
          (initial.mode === "demo"
            ? initial.projects.find((p) => p.id === "demo-project")
            : initial.projects[0]
          )?.id ||
          initial.projects[0]?.id ||
          "";
        if (code) {
          try {
            const joined = await post("/api/invites/join", { code });
            id = joined.id;
            setBoot(await api<Bootstrap>("/api/bootstrap"));
            notify("已加入项目");
          } catch (error) {
            notify(error instanceof Error ? error.message : "暂时无法加入项目");
          }
        }
        const params = new URLSearchParams(location.search);
        const requestedTask = miniAppTaskTarget(start, params);
        if (requestedTask) {
          setView("board");
          const task = await api<Task>(
            `/api/tasks/${encodeURIComponent(requestedTask)}`,
          );
          id = task.project_id;
          setSelected(task.id);
        }
        if (start === "settings") setView("settings");
        const requestedView = params.get("view");
        const requestedProject = params.get("project");
        if (lastProject && !requestedView && !start && !code && !requestedTask)
          setView("board");
        const projectTarget = requestedTask
          ? null
          : miniAppProjectTarget(start) ||
            (requestedProject
              ? miniAppProjectTarget(
                  `${requestedView === "tasks" ? "project" : requestedView}_${requestedProject}`,
                )
              : null);
        if (projectTarget) {
          const target = projectTarget.projectId;
          try {
            await api(`/api/projects/${encodeURIComponent(target)}`);
            if (!initial.projects.some((project) => project.id === target)) {
              await patch(
                `/api/projects/${encodeURIComponent(target)}/workspace`,
                { visible: true },
              );
              setBoot(await api<Bootstrap>("/api/bootstrap"));
              notify("项目已添加到我的项目");
            }
            id = target;
            setView(projectTarget.view);
          } catch (error) {
            setView("projects");
            notify(error instanceof Error ? error.message : "暂时无法打开项目");
          }
        }
        setProjectId(id);
      } catch (error) {
        setAuthError(error instanceof Error ? error.message : "无法连接服务");
      } finally {
        setInitialized(true);
        setLoading(false);
      }
    })();
  }, [notify]);
  useTelegramBackButton(
    !!(
      newTask ||
      selected ||
      modal ||
      projectMenuOpen ||
      ["members", "ai", "settings"].includes(view)
    ),
    () => {
      if (selected) setSelected(null);
      else if (newTask) setNewTask(null);
      else if (modal) setModal(null);
      else if (projectMenuOpen) setProjectMenuOpen(false);
      else setView(secondaryFrom.current);
    },
  );
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    },
    [],
  );
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [view, projectId]);
  const filterKey = JSON.stringify([
    projectId,
    debouncedSearch,
    assignee,
    priority,
    status,
    issueTypeFilter,
    onlyMine,
    view === "projects",
    Boolean(mobile),
  ]);
  if (pageState.key !== filterKey) setPageState({ key: filterKey, page: 1 });
  const page = pageState.key === filterKey ? pageState.page : 1;
  const projectQuery = useMemo(() => {
    const params = new URLSearchParams({
      brief: "1",
      page: String(page),
      page_size: "50",
      q: debouncedSearch,
      assignee,
      priority,
      status,
      issue_type: issueTypeFilter,
      mine: onlyMine ? "1" : "0",
    });
    return projectId && view !== "projects"
      ? `/api/projects/${encodeURIComponent(projectId)}?${params}`
      : "";
  }, [
    projectId,
    page,
    debouncedSearch,
    assignee,
    priority,
    status,
    issueTypeFilter,
    onlyMine,
    view === "projects",
  ]);
  const activeQuery = useRef(projectQuery);
  activeQuery.current = projectQuery;
  const receiveData = useCallback(
    (next: ProjectData) => {
      if (!mobile) {
        setData(next);
        return;
      }
      if (taskPages.current.key !== filterKey)
        taskPages.current = { key: filterKey, pages: new Map() };
      taskPages.current.pages.set(next.pagination.page, next.tasks);
      for (const cached of taskPages.current.pages.keys())
        if (cached > next.pagination.page)
          taskPages.current.pages.delete(cached);
      const tasks = new Map<string, TaskSummary>();
      for (const [, items] of [...taskPages.current.pages].sort(
        ([a], [b]) => a - b,
      ))
        for (const task of items) tasks.set(task.id, task);
      setData({ ...next, tasks: [...tasks.values()] });
    },
    [mobile, filterKey],
  );
  useEffect(() => {
    if (!selected) {
      setSelectedTask(null);
      return;
    }
    let alive = true;
    const controller = new AbortController();
    setSelectedTask(null);
    void api<Task>(`/api/tasks/${encodeURIComponent(selected)}`, {
      signal: controller.signal,
    })
      .then((task) => {
        if (alive && task.project_id === projectId) setSelectedTask(task);
      })
      .catch((error) => {
        if (alive) {
          fail(error);
          setSelected(null);
        }
      });
    return () => {
      alive = false;
      controller.abort();
    };
  }, [selected, projectId, fail]);
  const refresh = useCallback(async () => {
    if (projectQuery !== activeQuery.current) return;
    const version = ++workspaceVersion.current;
    const preferences = preferenceVersion.current;
    const [next, nextBoot, previousPages] = await Promise.all([
      projectQuery
        ? api<ProjectData>(projectQuery).catch((error) => {
            if (error instanceof ApiError && error.status === 404) return null;
            throw error;
          })
        : Promise.resolve(null),
      api<Bootstrap>("/api/bootstrap"),
      mobile && page > 1
        ? Promise.all(
            Array.from({ length: page - 1 }, (_, index) =>
              api<ProjectData>(
                projectQuery.replace(/([?&])page=\d+/, `$1page=${index + 1}`),
              ),
            ),
          )
        : Promise.resolve([]),
    ]);
    if (
      version !== workspaceVersion.current ||
      projectQuery !== activeQuery.current
    )
      return;
    if (preferences === preferenceVersion.current) setBoot(nextBoot);
    if (!nextBoot.projects.some((p) => p.id === projectId)) {
      setData(emptyData);
      setSelected(null);
      setNewTask(null);
      setModal((current) => (current === "manage" ? current : null));
      setProjectId(nextBoot.projects[0]?.id || "");
    } else if (next) {
      if (mobile)
        taskPages.current = {
          key: filterKey,
          pages: new Map(
            previousPages.map((result) => [
              result.pagination.page,
              result.tasks,
            ]),
          ),
        };
      receiveData(next);
    }
  }, [projectId, projectQuery, receiveData, mobile, page, filterKey]);
  const authenticated = initialized && boot !== null;
  useEffect(() => {
    if (!boot || !projectId || view === "projects") return;
    const key = `${boot.user.id}:${projectId}`;
    if (openedProject.current === key) return;
    openedProject.current = key;
    // Preserve the order of quick switches even when a previous write is slow.
    openingWrites.current = openingWrites.current
      .catch(() => {})
      .then(async () => {
        const result = await patch<{ last_opened_at: string }>(
          `/api/projects/${encodeURIComponent(projectId)}/preference`,
          { opened: true },
        );
        setBoot((current) =>
          current && current.user.id === boot.user.id
            ? {
                ...current,
                projects: current.projects.map((p) =>
                  p.id === projectId
                    ? { ...p, last_opened_at: result.last_opened_at }
                    : p,
                ),
              }
            : current,
        );
      })
      .catch(() => {});
  }, [boot?.user.id, projectId, view]);
  useEffect(() => {
    if (!authenticated) return;
    let alive = true;
    let fetching = false;
    const controller = new AbortController();
    setLoading(true);
    if (loadedProject.current !== projectId) {
      loadedProject.current = projectId;
      setData(emptyData);
    }
    const run = async () => {
      if (fetching) return;
      fetching = true;
      const version = ++workspaceVersion.current;
      const preferences = preferenceVersion.current;
      try {
        const [next, nextBoot] = await Promise.all([
          projectQuery
            ? api<ProjectData>(projectQuery, {
                signal: controller.signal,
              }).catch((error) => {
                if (error instanceof ApiError && error.status === 404)
                  return null;
                throw error;
              })
            : Promise.resolve(null),
          api<Bootstrap>("/api/bootstrap", { signal: controller.signal }),
        ]);
        if (!alive || version !== workspaceVersion.current) return;
        if (preferences === preferenceVersion.current) setBoot(nextBoot);
        if (!nextBoot.projects.some((p) => p.id === projectId)) {
          setData(emptyData);
          setSelected(null);
          setNewTask(null);
          setModal((current) => (current === "manage" ? current : null));
          setProjectId(nextBoot.projects[0]?.id || "");
        } else if (next) receiveData(next);
      } catch (error) {
        if (alive) fail(error);
      } finally {
        fetching = false;
        if (alive) setLoading(false);
      }
    };
    void run();
    const resume = () => {
      if (!document.hidden) void run();
    };
    const timer = setInterval(resume, 12000);
    window.addEventListener("focus", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      alive = false;
      workspaceVersion.current++;
      controller.abort();
      clearInterval(timer);
      window.removeEventListener("focus", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [projectId, projectQuery, receiveData, fail, authenticated]);
  const project = boot?.projects.find((p) => p.id === projectId);
  const canEdit = Boolean(
    project && project.role !== "viewer" && !project.repo_archived,
  );
  const members = new Map(data.members.map((u) => [u.id, u]));
  const assigneeOptions = useMemo(
    () => [
      ...new Map(
        [
          ...data.members,
          ...(data.assignee_options || []),
          ...data.tasks.flatMap((task) => task.assignees || []),
        ].map((person) => [person.id, person]),
      ).values(),
    ],
    [data.members, data.assignee_options, data.tasks],
  );
  const done = data.summary.done;
  const complete = data.summary.total
    ? Math.round((done / data.summary.total) * 100)
    : 0;
  const filtered = data.tasks;
  const syncingIssues = data.jobs.some(
    (job) =>
      job.kind === "github_import" &&
      ["pending", "processing"].includes(job.status),
  );
  const updateIssues = async () => {
    setBusy(true);
    try {
      if (project?.connection_status === "connected")
        await post(`/api/projects/${project.id}/issues/update`);
      await refresh();
      notify(
        project?.connection_status === "connected"
          ? "已安排后台更新，可继续查看任务"
          : "任务已刷新",
      );
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };
  const navigate = (next: View) => {
    if (
      next !== view &&
      documentNavigation.current &&
      !documentNavigation.current()
    )
      return;
    if (
      ["members", "ai", "settings"].includes(next) &&
      ["projects", "board", "docs", "activity"].includes(view)
    )
      secondaryFrom.current = view as
        "projects" | "board" | "docs" | "activity";
    setView(next);
    setProjectMenuOpen(false);
  };
  const navigationView = ["members", "ai", "settings"].includes(view)
    ? secondaryFrom.current
    : view;
  const chooseProject = (id: string) => {
    if (
      id !== projectId &&
      documentNavigation.current &&
      !documentNavigation.current()
    )
      return false;
    setProjectId(id);
    setSelected(null);
    setNewTask(null);
    setModal(null);
    setSearch("");
    setAssignee("");
    setPriority("");
    setStatus("");
    setIssueTypeFilter("");
    setOnlyMine(false);
    setProjectMenuOpen(false);
    return true;
  };
  const setFavorite = async (id: string, favorite: boolean) => {
    if (savingFavorites.has(id)) return;
    setSavingFavorites((current) => new Set(current).add(id));
    preferenceVersion.current++;
    try {
      const saved = await patch<{
        favorite: boolean;
        last_opened_at: string | null;
      }>(`/api/projects/${encodeURIComponent(id)}/preference`, { favorite });
      preferenceVersion.current++;
      setBoot((current) =>
        current
          ? {
              ...current,
              projects: current.projects.map((p) =>
                p.id === id ? { ...p, ...saved } : p,
              ),
            }
          : current,
      );
      notify(favorite ? "已添加到常用项目" : "已取消常用项目");
    } catch (error) {
      fail(error);
    } finally {
      setSavingFavorites((current) => {
        const next = new Set(current);
        next.delete(id);
        return next;
      });
    }
  };
  const updateTask = async (id: string, changes: unknown) => {
    const result = await patch<Task>(`/api/tasks/${id}`, changes);
    setSelectedTask((current) =>
      current?.id === id ? { ...current, ...result } : current,
    );
    setData((current) => ({
      ...current,
      tasks: current.tasks.map((task) =>
        task.id === id ? { ...task, ...result } : task,
      ),
    }));
    try {
      await refresh();
    } catch {
      notify("修改已保存，列表暂时无法刷新，请稍后重试");
      return result;
    }
    notify("任务已更新");
    return result;
  };
  const inviteMembers = async () => {
    if (!project) return;
    setBusy(true);
    try {
      const result = await post(`/api/projects/${project.id}/link-code`, {
        kind: "invite",
      });
      setInvite(result.url);
      setModal("invite");
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      notify("已复制到剪贴板");
    } catch {
      notify("请选中链接手动复制");
    }
  };
  const loadRepos = async () => {
    setBusy(true);
    try {
      setRepositories(await api("/api/github/repositories"));
      setModal("repositories");
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const connectGithub = async () => {
    setBusy(true);
    try {
      const result = await post("/api/github/connect");
      if (window.Telegram?.WebApp?.initData)
        window.Telegram.WebApp.openLink(result.url);
      else window.location.assign(result.url);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const syncProjects = async () => {
    setBusy(true);
    try {
      await post("/api/github/sync");
      await refresh();
      notify(
        boot?.mode === "demo"
          ? "已演示导入仓库项目和团队成员，可查看 service-api 项目"
          : "项目与成员同步已加入队列，将自动更新列表",
      );
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };
  if (!boot)
    return (
      <div className="auth-screen">
        <div className="auth-logo">
          <Send size={30} />
        </div>
        <h1>TGFlow</h1>
        <p>
          {authError
            ? new URLSearchParams(location.search).get("github") === "connected"
              ? "GitHub 账号已连接，请回到 Telegram 继续使用。"
              : authError
            : "正在打开你的工作空间…"}
        </p>
        {authError ? (
          <UiButton className="button" onClick={() => location.reload()}>
            重新连接
          </UiButton>
        ) : (
          <LoaderCircle className="loading-spin" />
        )}
      </div>
    );
  return (
    <div className="app-shell" data-view={view}>
      <aside className="sidebar">
        <a
          href="#"
          className="brand"
          onClick={(e) => {
            e.preventDefault();
            navigate("projects");
          }}
        >
          <span className="brand-mark">
            <Send size={22} />
          </span>
          <span>
            TGFlow<span className="brand-dot">.</span>
          </span>
        </a>
        <div className="workspace">
          <div className="workspace-icon">
            <Layers3 size={19} />
          </div>
          <div>
            <strong>产品研发空间</strong>
            <span>{boot.projects.length} 个项目</span>
          </div>
          <span className="workspace-badge">团队</span>
        </div>
        <nav className="main-nav" aria-label="工作空间导航">
          <UiButton
            className={navigationView === "projects" ? "active" : ""}
            onClick={() => navigate("projects")}
          >
            <FolderKanban size={18} />
            项目<span className="nav-count">{boot.projects.length}</span>
          </UiButton>
          <UiButton
            className={navigationView === "board" ? "active" : ""}
            onClick={() => navigate("board")}
          >
            <List size={18} />
            任务
          </UiButton>
          <UiButton
            className={navigationView === "docs" ? "active" : ""}
            onClick={() => navigate("docs")}
          >
            <FileText size={18} />
            文档
          </UiButton>
          <UiButton
            className={navigationView === "activity" ? "active" : ""}
            onClick={() => navigate("activity")}
          >
            <Radio size={18} />
            动态
          </UiButton>
        </nav>
        {project && view !== "projects" ? (
          <div className="sidebar-current-project">
            <small>当前项目</small>
            <strong>{project.name}</strong>
            <UiButton
              className="text-button"
              onClick={() => setProjectMenuOpen(true)}
            >
              <MoreHorizontal size={18} />
              项目操作
            </UiButton>
          </div>
        ) : null}
        <div className="sidebar-bottom">
          <div className="connection-mini">
            <span className="pulse-dot" />
            <span>{boot.mode === "demo" ? "演示工作区" : "工作区已就绪"}</span>
            <span className="version">v{appVersion}</span>
          </div>
          <p>
            {boot.mode === "demo"
              ? "用示例项目体验完整的任务协作流程。"
              : "讨论、任务与代码进展，在这里汇合。"}
          </p>
          <div className="profile">
            <Avatar user={boot.user} />
            <div>
              <strong>{boot.user.name}</strong>
              <span>{boot.mode === "demo" ? "演示账号" : "Telegram 成员"}</span>
            </div>
          </div>
        </div>
      </aside>
      <div className="workspace-main">
        {view === "projects" ? (
          <header className="projects-topbar">
            <span>
              <Send size={22} />
              TGFlow
            </span>
            <span>{boot.user.name}</span>
          </header>
        ) : (
          <header className="topbar">
            <div className="breadcrumb">
              <UiButton
                className="mobile-project-switch"
                aria-label={`切换项目，当前${project?.name || "未选择项目"}`}
                onClick={() => setModal("switch")}
              >
                <span
                  className="project-square"
                  style={{ background: project?.color || "#2993e0" }}
                />
                <strong>{project?.name || "选择项目"}</strong>
                <ChevronDown size={18} />
              </UiButton>
              <FolderKanban size={17} />
              <span className="breadcrumb-root">工作空间</span>
              <ChevronRight size={13} />
              <strong>{project?.name || "项目"}</strong>
            </div>
            <div className="topbar-actions">
              <TextInput
                className="ui-global-search"
                leftSection={<Search size={16} />}
                ref={searchRef}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (
                    view === "settings" ||
                    view === "activity" ||
                    view === "members" ||
                    view === "ai" ||
                    view === "docs"
                  )
                    navigate("board");
                }}
                placeholder="搜索任务…"
                aria-label="搜索任务"
              />
              <UiButton
                className="icon-button"
                aria-label="项目操作"
                onClick={() => setProjectMenuOpen(true)}
              >
                <MoreHorizontal size={20} />
              </UiButton>
              <Avatar user={boot.user} size="small" />
            </div>
          </header>
        )}
        <main>
          {view === "board" ? (
            <nav
              className="favorite-project-switcher"
              aria-label="常用项目快捷切换"
            >
              <span>
                <Star size={16} />
                常用
              </span>
              <div className="favorite-project-chips">
                {boot.projects
                  .filter((p) => p.favorite)
                  .map((p) => (
                    <UiButton
                      className={`favorite-project-chip ${p.id === projectId ? "current" : ""}`}
                      key={p.id}
                      aria-current={p.id === projectId ? "page" : undefined}
                      onClick={() => chooseProject(p.id)}
                    >
                      <span
                        className="project-square"
                        style={{ background: p.color }}
                      />
                      {p.name}
                    </UiButton>
                  ))}
                {!boot.projects.some((p) => p.favorite) ? (
                  <span className="favorite-empty-label">
                    将常用项目加星，方便快速切换
                  </span>
                ) : null}
              </div>
              <UiButton
                className="text-button"
                onClick={() => setModal("switch")}
              >
                全部项目
                <ChevronDown size={15} />
              </UiButton>
            </nav>
          ) : null}
          {view !== "projects" ? (
            <div className="page-heading">
              <div>
                <div className="page-eyebrow">
                  <span
                    className="project-square"
                    style={{ background: project?.color || "#2993e0" }}
                  />
                  团队项目
                  {boot.mode === "demo" ? (
                    <span className="demo-label">演示</span>
                  ) : null}
                </div>
                <h1>
                  {view === "members" ? (
                    "团队成员"
                  ) : view === "ai" ? (
                    "AI 项目助手"
                  ) : view === "activity" ? (
                    "项目动态"
                  ) : view === "settings" ? (
                    "集成与设置"
                  ) : view === "docs" ? (
                    "项目文档"
                  ) : (
                    <>
                      <span className="desktop-project-title">
                        {project?.name || "开始你的第一个项目"}
                      </span>
                      <span className="mobile-page-title">
                        {project ? "项目任务" : "创建第一个项目"}
                      </span>
                    </>
                  )}
                </h1>
                <p>
                  {view === "members"
                    ? "GitHub 团队与 Telegram 身份，在这里关联。"
                    : view === "ai"
                      ? "读懂当前进展，把下一步变成可执行的任务。"
                      : view === "activity"
                        ? "团队协作与代码交付的每一次进展。"
                        : view === "settings"
                          ? "连接团队的讨论与代码，让进展自动汇合。"
                          : view === "docs"
                            ? "共享项目说明、操作流程和文件，让团队随时查阅。"
                            : project?.description ||
                              "创建一个项目，开始分配任务和跟踪进度。"}
                </p>
              </div>
              {view !== "docs" ? (
                <div className="heading-actions">
                  {project ? (
                    <>
                      <div className="avatar-stack">
                        {data.members.slice(0, 4).map((u) => (
                          <Avatar key={u.id} user={u} size="small" />
                        ))}
                      </div>
                      {project.role === "owner" ? (
                        <UiButton
                          className="button secondary invite-button"
                          disabled={busy}
                          onClick={() => void inviteMembers()}
                        >
                          <Users size={16} />
                          邀请成员
                        </UiButton>
                      ) : null}
                      <UiButton
                        className="button primary"
                        disabled={!canEdit}
                        title={!canEdit ? "当前项目为只读" : undefined}
                        onClick={() => setNewTask("todo")}
                      >
                        <Plus size={17} />
                        新建任务
                      </UiButton>
                    </>
                  ) : (
                    <UiButton
                      className="button primary"
                      onClick={() => setModal("project")}
                    >
                      <Plus size={17} />
                      新建项目
                    </UiButton>
                  )}
                </div>
              ) : null}
            </div>
          ) : null}
          {view === "projects" ? (
            <Suspense
              fallback={<div className="loading-state">正在载入项目…</div>}
            >
              <ProjectsView
                boot={boot}
                selected={projectId}
                busy={busy}
                onOpen={(id) => {
                  if (chooseProject(id)) navigate("board");
                }}
                onActions={(id) => {
                  if (chooseProject(id)) setProjectMenuOpen(true);
                }}
                onManage={() => setModal("manage")}
                onCreate={() => setModal("project")}
                onConnect={() => void connectGithub()}
                onSync={() => void syncProjects()}
                onFavorite={(id, favorite) => void setFavorite(id, favorite)}
                savingFavorite={savingFavorites}
              />
            </Suspense>
          ) : project ? (
            <>
              <div className="project-strip">
                <div className="integrations-inline">
                  <UiButton onClick={() => navigate("settings")}>
                    <Github size={15} />
                    <span>
                      {project.repo_name
                        ? `${project.repo_owner}/${project.repo_name}`
                        : "连接 GitHub"}
                    </span>
                    <span
                      className={`tiny-dot ${project.connection_status === "connected" ? "green" : "gray"}`}
                    />
                  </UiButton>
                  <span className="strip-divider" />
                  <UiButton onClick={() => navigate("settings")}>
                    <Send size={14} />
                    <span>{project.chat_title || "连接项目群"}</span>
                    <span
                      className={`tiny-dot ${project.chat_id ? "green" : "gray"}`}
                    />
                  </UiButton>
                </div>
                <span className="sync-note">
                  {boot.mode === "demo" ? (
                    "示例数据 · 操作会保存到本地"
                  ) : (
                    <>
                      <span className="tiny-dot green" />
                      后台自动同步
                    </>
                  )}
                </span>
              </div>
              {project.repo_archived || project.role === "viewer" ? (
                <p className="readonly-note">
                  {project.repo_archived
                    ? "仓库已归档，项目为只读。"
                    : "你拥有只读权限，可以查看任务和团队成员。"}
                </p>
              ) : null}
              {view === "board" ? (
                <>
                  <div className="progress-section">
                    <div className="progress-copy">
                      <span className="progress-icon">
                        <CheckCheck size={20} />
                      </span>
                      <div>
                        <strong>已导入任务完成度</strong>
                        <span>
                          已完成 {done} / {data.summary.total} 项任务
                        </span>
                      </div>
                    </div>
                    <div className="progress-track-group">
                      <div className="progress-track">
                        <div style={{ width: `${complete}%` }} />
                      </div>
                      <strong>{complete}%</strong>
                    </div>
                    <div className="review-summary">
                      <GitPullRequest size={17} />
                      <span>{data.summary.review} 项等待代码评审</span>
                    </div>
                  </div>
                  <Tabs
                    value={status || "all"}
                    onChange={(value) =>
                      setStatus(value === "all" ? "" : value || "")
                    }
                    className="task-status-tabs"
                    keepMounted={false}
                  >
                    <Tabs.List aria-label="任务状态">
                      <Tabs.Tab value="all">
                        全部{" "}
                        <span className="task-tab-count">
                          {Object.values(data.pagination.status_counts).reduce(
                            (sum, n) => sum + n,
                            0,
                          )}
                        </span>
                      </Tabs.Tab>
                      {statuses.map((s) => (
                        <Tabs.Tab key={s} value={s}>
                          {names[s]}{" "}
                          <span className="task-tab-count">
                            {data.pagination.status_counts[s]}
                          </span>
                        </Tabs.Tab>
                      ))}
                    </Tabs.List>
                    <Tabs.Panel value={status || "all"}>
                      <div className="board-toolbar">
                        <div className="task-ownership-filter">
                          <Checkbox
                            label="只看我的"
                            checked={onlyMine}
                            onChange={(event) =>
                              setOnlyMine(event.currentTarget.checked)
                            }
                          />
                          <span className="task-results-count">
                            {data.pagination.total} 项任务
                          </span>
                        </div>
                        <div className="toolbar-right">
                          <UiButton
                            className={`text-button ${filters ? "chosen" : ""}`}
                            onClick={() => setFilters((v) => !v)}
                            aria-expanded={filters}
                          >
                            <Filter size={15} />
                            筛选
                            {assignee ||
                            priority ||
                            status ||
                            issueTypeFilter ? (
                              <span className="filter-dot" />
                            ) : null}
                          </UiButton>
                          <div className="issue-update-actions">
                            <UiButton
                              className="text-button"
                              aria-label="更新 GitHub Issue"
                              disabled={busy || syncingIssues}
                              onClick={() => void updateIssues()}
                            >
                              <RefreshCw
                                size={15}
                                className={
                                  busy || syncingIssues ? "loading-spin" : ""
                                }
                              />
                              <span>{syncingIssues ? "更新中" : "更新"}</span>
                            </UiButton>
                          </div>
                        </div>
                      </div>
                      {filters ? (
                        <div className="filter-bar">
                          <NativeSelect
                            label="任务类型"
                            value={issueTypeFilter}
                            onChange={(e) => setIssueTypeFilter(e.target.value)}
                          >
                            <option value="">全部类型</option>
                            <option value="none">无类型</option>
                            {data.summary.issue_types.map((value) => (
                              <option key={value} value={value}>
                                {value}
                              </option>
                            ))}
                          </NativeSelect>
                          <NativeSelect
                            label="负责人"
                            value={assignee}
                            onChange={(e) => setAssignee(e.target.value)}
                          >
                            <option value="">所有成员</option>
                            <option value="unassigned">未分配</option>
                            {assigneeOptions.map((u) => (
                              <option key={u.id} value={u.id}>
                                {assigneeName(u)}
                              </option>
                            ))}
                          </NativeSelect>
                          <NativeSelect
                            label="优先级"
                            value={priority}
                            onChange={(e) => setPriority(e.target.value)}
                          >
                            <option value="">全部优先级</option>
                            {Object.entries(priorities).map(([v, t]) => (
                              <option key={v} value={v}>
                                {t}
                              </option>
                            ))}
                          </NativeSelect>

                          <UiButton
                            className="text-button"
                            onClick={() => {
                              setAssignee("");
                              setPriority("");
                              setStatus("");
                              setIssueTypeFilter("");
                              setSearch("");
                            }}
                          >
                            清除筛选
                          </UiButton>
                        </div>
                      ) : null}
                      {loading &&
                      !(
                        mobile &&
                        page > 1 &&
                        taskPages.current.key === filterKey &&
                        data.tasks.length
                      ) ? (
                        <div className="loading-state">
                          <LoaderCircle className="loading-spin" />
                          正在载入任务…
                        </div>
                      ) : filtered.length === 0 ? (
                        <div className="empty-state">
                          <Search size={28} />
                          <strong>
                            {data.summary.total
                              ? "没有找到匹配的任务"
                              : "项目还没有任务"}
                          </strong>
                          <p>
                            {data.summary.total
                              ? "试试其他关键词，或切换任务状态、清除筛选条件。"
                              : canEdit
                                ? "创建第一项任务，开始安排项目进度。"
                                : "任务会在这里展示，可以到集成与设置查看仓库同步状态。"}
                          </p>
                          <UiButton
                            className="button secondary"
                            onClick={() => {
                              if (!data.summary.total) {
                                if (canEdit) setNewTask("todo");
                                else navigate("settings");
                                return;
                              }
                              setSearch("");
                              setAssignee("");
                              setPriority("");
                              setStatus("");
                              setIssueTypeFilter("");
                            }}
                          >
                            {data.summary.total
                              ? "清除筛选"
                              : canEdit
                                ? "新建任务"
                                : "查看同步设置"}
                          </UiButton>
                        </div>
                      ) : (
                        <TaskList
                          mobile={Boolean(mobile)}
                          tasks={filtered}
                          project={project}
                          members={data.members}
                          currentUserId={boot.user.id}
                          onOpen={setSelected}
                          onPatch={updateTask}
                        />
                      )}
                      <div className="task-pagination" aria-label="任务分页">
                        <span className="muted">
                          {mobile
                            ? `已显示 ${filtered.length} / ${data.pagination.total} 项`
                            : `第 ${data.pagination.page} / ${data.pagination.pages} 页 · 每页 50 项`}
                        </span>
                        {mobile ? (
                          data.pagination.page < data.pagination.pages ? (
                            <UiButton
                              className="button secondary"
                              disabled={loading}
                              onClick={() =>
                                setPageState({
                                  key: filterKey,
                                  page: data.pagination.page + 1,
                                })
                              }
                            >
                              {loading ? (
                                <LoaderCircle
                                  size={16}
                                  className="loading-spin"
                                />
                              ) : null}
                              加载更多
                            </UiButton>
                          ) : (
                            <span className="muted">
                              已显示当前范围全部任务
                            </span>
                          )
                        ) : (
                          <div>
                            <UiButton
                              className="button secondary"
                              disabled={loading || data.pagination.page <= 1}
                              onClick={() =>
                                setPageState({
                                  key: filterKey,
                                  page: data.pagination.page - 1,
                                })
                              }
                            >
                              <ChevronLeft size={16} />
                              上一页
                            </UiButton>
                            <UiButton
                              className="button secondary"
                              disabled={
                                loading ||
                                data.pagination.page >= data.pagination.pages
                              }
                              onClick={() =>
                                setPageState({
                                  key: filterKey,
                                  page: data.pagination.page + 1,
                                })
                              }
                            >
                              下一页
                              <ChevronRight size={16} />
                            </UiButton>
                          </div>
                        )}
                      </div>
                      {syncingIssues ? (
                        <p className="task-sync-hint" role="status">
                          <LoaderCircle size={14} className="loading-spin" />
                          正在后台分批同步 Issue 和 PR，可继续查看和编辑任务。
                        </p>
                      ) : null}
                      <div className="board-footer">
                        <span>
                          <span className="tiny-dot blue" />
                          支持从 Telegram 项目群创建任务
                        </span>
                        <UiButton
                          className="text-button"
                          onClick={() => navigate("activity")}
                        >
                          查看项目动态
                          <ChevronRight size={14} />
                        </UiButton>
                      </div>
                    </Tabs.Panel>
                  </Tabs>
                </>
              ) : view === "docs" ? (
                <Suspense
                  fallback={
                    <div className="loading-state">正在载入项目文档…</div>
                  }
                >
                  <ProjectDocuments
                    key={project.id}
                    project={project}
                    notify={notify}
                    onGuardChange={setDocumentGuard}
                  />
                </Suspense>
              ) : view === "ai" ? (
                <Suspense
                  fallback={
                    <div className="loading-state">
                      <LoaderCircle className="loading-spin" />
                      正在载入 AI 助手…
                    </div>
                  }
                >
                  <AiPanel
                    key={project.id}
                    project={project}
                    boot={boot}
                    members={data.members}
                    refresh={refresh}
                    notify={notify}
                    openTask={setSelected}
                  />
                </Suspense>
              ) : view === "members" ? (
                <MembersView
                  key={project.id}
                  project={project}
                  members={data.members}
                  currentUserId={boot.user.id}
                  onClassify={async (memberId, values) => {
                    await patch(
                      `/api/projects/${project.id}/members/${memberId}`,
                      { disciplines: values },
                    );
                    await refresh();
                    notify("成员分类已保存");
                  }}
                  busy={busy}
                  teamPickerOpened={modal === "member-team"}
                  onTeamPickerOpen={(opened) =>
                    setModal(opened ? "member-team" : null)
                  }
                  onSync={async () => {
                    setBusy(true);
                    try {
                      await post(`/api/projects/${project.id}/sync`);
                      await refresh();
                      notify(
                        boot.mode === "demo"
                          ? "演示成员已同步"
                          : "成员同步已加入队列",
                      );
                    } catch (error) {
                      fail(error);
                    } finally {
                      setBusy(false);
                    }
                  }}
                />
              ) : view === "activity" ? (
                <ActivityView
                  activities={data.activities}
                  open={(id) => setSelected(id)}
                />
              ) : (
                <SettingsView
                  key={project.id}
                  project={project}
                  boot={boot}
                  jobs={data.jobs}
                  busy={busy}
                  refresh={refresh}
                  notify={notify}
                  fail={fail}
                  loadRepos={loadRepos}
                  manageProjects={() => navigate("projects")}
                  simulate={async (type) => {
                    await post(`/api/projects/${project.id}/demo-event`, {
                      type,
                    });
                    await refresh();
                    notify("演示事件已处理，可在任务和项目动态中查看");
                  }}
                />
              )}
            </>
          ) : (
            <div className="empty-state">
              <FolderKanban size={32} />
              <strong>把团队的下一件事放进来</strong>
              <p>
                创建项目，或连接 GitHub 自动导入有权访问的仓库项目和团队成员。
              </p>
              <UiButton
                className="button secondary"
                onClick={() => setModal("manage")}
              >
                管理与恢复项目
              </UiButton>
              <UiButton
                className="button secondary"
                disabled={busy || !boot.githubConfigured}
                onClick={() =>
                  void (boot.user.github_login
                    ? syncProjects()
                    : connectGithub())
                }
              >
                {boot.user.github_login ? "同步 GitHub 项目" : "连接 GitHub"}
              </UiButton>
              {boot.githubSyncJobs.map((job) => (
                <p key={job.id} className="sync-warning" role="status">
                  {job.last_error ||
                    "GitHub 项目与成员正在自动同步，完成后将出现在项目列表。"}
                </p>
              ))}
            </div>
          )}
        </main>
      </div>
      <nav className="mobile-tabbar" aria-label="移动端主导航">
        {(
          [
            { id: "projects", label: "项目", icon: FolderKanban },
            { id: "board", label: "任务", icon: List },
            { id: "docs", label: "文档", icon: FileText },
            { id: "activity", label: "动态", icon: Radio },
          ] as const
        ).map((item) => (
          <UiButton
            key={item.id}
            aria-current={navigationView === item.id ? "page" : undefined}
            onClick={() => navigate(item.id)}
          >
            <item.icon size={22} />
            <span>{item.label}</span>
          </UiButton>
        ))}
      </nav>
      {projectMenuOpen && project ? (
        <Overlay title={project.name} onClose={() => setProjectMenuOpen(false)}>
          <div className="mobile-more dialog-content project-actions-sheet">
            <p className="muted">
              {project.repo_owner
                ? `${project.repo_owner}/${project.repo_name}`
                : "本地项目"}
            </p>
            <UiButton onClick={() => navigate("docs")}>
              <FileText size={21} />
              <span>
                项目文档<small>查看说明、编辑文档与上传附件</small>
              </span>
              <ChevronRight size={18} />
            </UiButton>
            <UiButton onClick={() => navigate("members")}>
              <Users size={21} />
              <span>
                团队成员<small>查看负责人、成员和权限</small>
              </span>
              <ChevronRight size={18} />
            </UiButton>
            <UiButton onClick={() => navigate("ai")}>
              <Sparkles size={21} />
              <span>
                AI 助手<small>整理需求、拆解任务与项目摘要</small>
              </span>
              <ChevronRight size={18} />
            </UiButton>
            <UiButton onClick={() => navigate("settings")}>
              <Settings2 size={21} />
              <span>
                集成与设置<small>仓库、Telegram 群与提醒</small>
              </span>
              <ChevronRight size={18} />
            </UiButton>
            {project.role === "owner" ? (
              <UiButton
                disabled={busy}
                onClick={() => {
                  setProjectMenuOpen(false);
                  void inviteMembers();
                }}
              >
                <Link2 size={21} />
                <span>邀请项目成员</span>
                <ChevronRight size={18} />
              </UiButton>
            ) : null}
          </div>
        </Overlay>
      ) : null}
      {modal === "switch" ? (
        <Overlay title="切换项目" onClose={() => setModal(null)}>
          <Suspense
            fallback={<div className="loading-state">正在加载项目…</div>}
          >
            <ProjectPicker
              projects={boot.projects}
              selected={projectId}
              onSelect={(id) => {
                if (chooseProject(id)) navigate("board");
              }}
              onManage={() => setModal("manage")}
              onFavorite={(id, favorite) => void setFavorite(id, favorite)}
              savingFavorite={savingFavorites}
            />
          </Suspense>
        </Overlay>
      ) : null}
      {toast ? (
        <Notification
          className="ui-toast"
          role="status"
          icon={<Check size={18} />}
          onClose={() => setToast("")}
          closeButtonProps={{ "aria-label": "关闭提示" }}
        >
          {toast}
        </Notification>
      ) : null}
      {newTask && project ? (
        <TaskEditor
          mobile={Boolean(mobile)}
          currentUserId={boot.user.id}
          members={data.members}
          demo={boot.mode === "demo"}
          project={project}
          initialStatus={newTask}
          onClose={() => setNewTask(null)}
          onSave={async (input) => {
            await post(`/api/projects/${project.id}/tasks`, input);
            await refresh();
            setNewTask(null);
            notify(
              boot.mode === "live" && input.sync_to_github && project.repo_id
                ? "任务已创建，正在同步 GitHub"
                : "任务已创建",
            );
          }}
        />
      ) : null}
      {selected && selectedTask?.id !== selected && project ? (
        <Overlay title="任务详情" onClose={() => setSelected(null)}>
          <div className="loading-state">
            <LoaderCircle className="loading-spin" />
            正在载入详情…
          </div>
        </Overlay>
      ) : null}
      {selectedTask && selectedTask.id === selected && project ? (
        <TaskEditor
          mobile={Boolean(mobile)}
          key={selectedTask.id}
          currentUserId={boot.user.id}
          demo={boot.mode === "demo"}
          members={data.members}
          project={project}
          task={selectedTask}
          activities={data.activities.filter(
            (a) => a.task_id === selectedTask.id,
          )}
          onClose={() => setSelected(null)}
          onSave={async (input) => {
            return updateTask(selectedTask.id, input);
          }}
        />
      ) : null}
      {modal === "invite" ? (
        <Overlay title="邀请成员加入项目" onClose={() => setModal(null)}>
          <div className="dialog-content">
            <div className="invite-art">
              <Users size={35} />
            </div>
            <h2>一起把事情做好</h2>
            <p className="muted">
              {project?.members_sync_enabled
                ? "成员需先连接 GitHub，且拥有对应仓库访问权限。邀请链接不会额外授予权限。链接有效期为 7 天。"
                : "将邀请链接分享给团队成员。链接有效期为 7 天。"}
            </p>
            <div className="copy-field">
              <input aria-label="项目邀请链接" readOnly value={invite} />
              <UiButton
                className="button secondary"
                onClick={() => void copy(invite)}
              >
                <Copy size={16} />
                复制
              </UiButton>
            </div>
          </div>
        </Overlay>
      ) : null}
      {modal === "project" ? (
        <ProjectModal
          onClose={() => setModal(null)}
          onCreate={async (name, description) => {
            const result = await post("/api/projects", { name, description });
            const b = await api<Bootstrap>("/api/bootstrap");
            setBoot(b);
            if (chooseProject(result.id)) navigate("board");
            setModal(null);
            notify("项目已创建");
          }}
        />
      ) : null}
      {modal === "manage" ? (
        <Overlay title="管理我的项目" wide onClose={() => setModal(null)}>
          <Suspense
            fallback={<div className="dialog-content">正在打开项目管理…</div>}
          >
            <ProjectManager
              boot={boot}
              currentProjectId={projectId}
              onOpen={(id) => {
                if (chooseProject(id)) navigate("board");
              }}
              onCreate={() => setModal("project")}
              onSync={syncProjects}
              onChange={async (ids, visible) => {
                await patch("/api/projects/workspace", {
                  project_ids: ids,
                  visible,
                });
                await refresh();
                notify(
                  visible
                    ? `已将 ${ids.length} 个项目添加到我的项目`
                    : `已移除 ${ids.length} 个项目，可在项目管理中重新添加`,
                );
              }}
            />
          </Suspense>
        </Overlay>
      ) : null}
      {modal === "repositories" && project ? (
        <RepoModal
          project={project}
          repos={repositories}
          onClose={() => setModal(null)}
          onBind={async (repo) => {
            await post(`/api/projects/${project.id}/github`, {
              repository_id: repo.id,
              installation_id: repo.installation_id,
            });
            await refresh();
            setModal(null);
            notify(
              boot.mode === "demo"
                ? "已绑定演示仓库"
                : "仓库已绑定，正在导入 Issue 和 PR",
            );
          }}
        />
      ) : null}
    </div>
  );
}

function ActivityView({
  activities,
  open,
}: {
  activities: Activity[];
  open: (id: string) => void;
}) {
  return (
    <section className="activity-panel">
      <div className="section-head">
        <h2>最近动态</h2>
        <span>{activities.length} 条记录</span>
      </div>
      {activities.length ? (
        activities.map((a) => {
          const Icon =
            a.kind === "merged"
              ? GitMerge
              : a.kind === "ci_fail"
                ? AlertCircle
                : a.kind === "review"
                  ? GitPullRequest
                  : a.kind === "connected"
                    ? Link2
                    : a.kind === "created"
                      ? Plus
                      : CircleCheck;
          return (
            <div className="activity-item" key={a.id}>
              <div className={`activity-icon ${a.kind}`}>
                <Icon size={18} />
              </div>
              <div className="activity-copy">
                <p>
                  <strong>{a.actor}</strong> {a.title}
                </p>
                {a.task_id ? (
                  <UiButton onClick={() => open(a.task_id!)}>
                    {a.detail}
                  </UiButton>
                ) : (
                  <span>{a.detail}</span>
                )}
                <small>{relative(a.created_at)}</small>
              </div>
              {a.url ? (
                <a
                  aria-label="在 GitHub 查看动态"
                  className="icon-button"
                  href={safeUrl(a.url)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ArrowUpRight size={17} />
                </a>
              ) : null}
            </div>
          );
        })
      ) : (
        <div className="empty-state">
          <Radio size={28} />
          <strong>项目的第一步，从创建任务开始</strong>
          <p>任务更新与 GitHub 进展会记录在这里。</p>
        </div>
      )}
    </section>
  );
}

const roleLabels = { owner: "管理员", member: "可编辑", viewer: "只读" };
function MembersView({
  project,
  members,
  currentUserId,
  onClassify,
  busy,
  teamPickerOpened,
  onTeamPickerOpen,
  onSync,
}: {
  project: Project;
  members: Member[];
  currentUserId: string;
  onClassify: (memberId: string, values: Discipline[]) => Promise<void>;
  busy: boolean;
  teamPickerOpened: boolean;
  onTeamPickerOpen: (opened: boolean) => void;
  onSync: () => Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [team, setTeam] = useState("");
  const [discipline, setDiscipline] = useState("all");
  const [editing, setEditing] = useState<string | null>(null);
  const [selected, setSelected] = useState<Discipline[]>([]);
  const [savingClass, setSavingClass] = useState(false);
  const [classError, setClassError] = useState("");
  const teams = [
    ...new Map(
      members
        .flatMap((member) => member.github_teams)
        .map((item) => [item.id, item]),
    ).values(),
  ];
  const matching = members.filter(
    (member) =>
      `${member.name} ${member.github_login || ""} ${member.username || ""}`
        .toLowerCase()
        .includes(query.trim().toLowerCase()) &&
      (!team || member.github_teams.some((item) => item.id === team)) &&
      (discipline === "all" ||
        (discipline === "none"
          ? !member.disciplines?.length
          : member.disciplines?.includes(discipline as Discipline))),
  );
  return (
    <section className="members-panel">
      <div className="members-overview">
        <div>
          <strong>{members.length}</strong>
          <span>项目成员</span>
        </div>
        <div>
          <strong>{teams.length}</strong>
          <span>GitHub Teams</span>
        </div>
        <div>
          <strong>
            {members.filter((member) => member.telegram_id).length}
          </strong>
          <span>已关联 Telegram</span>
        </div>
      </div>
      <div className="members-toolbar">
        <div>
          <h2>项目团队</h2>
          <p>
            {project.members_sync_enabled
              ? `成员与权限跟随 GitHub · ${project.members_synced_at ? `最近同步 ${relative(project.members_synced_at)}` : "等待首次同步"}`
              : "通过邀请加入的项目成员"}
          </p>
        </div>
        {project.role === "owner" && project.repo_id ? (
          <UiButton
            className="button ghost member-sync"
            aria-label="同步项目成员"
            disabled={busy}
            onClick={() => void onSync()}
          >
            <RefreshCw size={15} />
            同步
          </UiButton>
        ) : null}
      </div>
      {project.sync_error ? (
        <p className="sync-warning" role="alert">
          <AlertCircle size={16} />
          {project.sync_error}
        </p>
      ) : null}
      {project.team_sync_warning ? (
        <p className="sync-warning">
          <AlertCircle size={16} />
          {project.team_sync_warning}
        </p>
      ) : null}
      <div className="member-filters">
        <TextInput
          className="ui-member-search"
          leftSection={<Search size={18} />}
          aria-label="搜索团队成员"
          placeholder="搜索姓名或账号"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <MemberTeamFilter
          opened={teamPickerOpened}
          onOpenedChange={onTeamPickerOpen}
          teams={teams.map((item) => ({
            ...item,
            count: members.filter((member) =>
              member.github_teams.some((team) => team.id === item.id),
            ).length,
          }))}
          value={team}
          total={members.length}
          onChange={setTeam}
        />
      </div>
      <Tabs
        value={discipline}
        onChange={(value) => setDiscipline(value || "all")}
        className="discipline-tabs"
      >
        <Tabs.List aria-label="按成员分类筛选">
          <Tabs.Tab value="all">全部</Tabs.Tab>
          {disciplines.map((d) => (
            <Tabs.Tab key={d} value={d}>
              {disciplineNames[d]}
            </Tabs.Tab>
          ))}
          <Tabs.Tab value="none">未分类</Tabs.Tab>
        </Tabs.List>
      </Tabs>
      <div className="member-results">
        <span role="status">
          {query.trim() || team || discipline !== "all"
            ? "筛选结果"
            : "全部成员"}{" "}
          · {matching.length} 人
        </span>
        {query || team || discipline !== "all" ? (
          <UiButton
            className="member-filter-reset"
            onClick={() => {
              setQuery("");
              setTeam("");
              setDiscipline("all");
            }}
          >
            清除筛选
          </UiButton>
        ) : null}
      </div>
      <div className="member-table" role="table" aria-label="项目成员">
        <div className="member-row member-table-head" role="row">
          <span role="columnheader">成员</span>
          <span role="columnheader">分类 / 团队</span>
          <span role="columnheader">权限</span>
          <span role="columnheader">Telegram</span>
        </div>
        {matching.map((member) => (
          <div className="member-row" key={member.id} role="row">
            <div className="member-identity" role="cell">
              <Avatar user={member} />
              <div>
                <strong>{member.name}</strong>
                {member.github_login ? (
                  <a
                    href={`https://github.com/${encodeURIComponent(member.github_login)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    @{member.github_login}
                    <ArrowUpRight size={12} />
                  </a>
                ) : (
                  <small>
                    {member.username ? `@${member.username}` : "Telegram 成员"}
                  </small>
                )}
              </div>
            </div>
            <div className="member-teams" role="cell">
              <div className="member-disciplines">
                {(member.disciplines || []).map((d) => (
                  <span className={`discipline-chip ${d}`} key={d}>
                    {disciplineNames[d]}
                  </span>
                ))}
                {!member.disciplines?.length ? <small>未分类</small> : null}
                {project.role !== "viewer" &&
                (project.role === "owner" || member.id === currentUserId) ? (
                  <UiButton
                    className="discipline-edit"
                    aria-label={`设置 ${member.name} 的成员分类`}
                    aria-expanded={editing === member.id}
                    onClick={() => {
                      setEditing(editing === member.id ? null : member.id);
                      setSelected(member.disciplines || []);
                      setClassError("");
                    }}
                  >
                    设置分类
                  </UiButton>
                ) : null}
              </div>
              {member.github_teams.length ? (
                member.github_teams.map((item) => (
                  <span key={item.id}>{item.name}</span>
                ))
              ) : (
                <small>
                  {member.source === "github" ? "GitHub 仓库成员" : "项目邀请"}
                </small>
              )}
            </div>
            <div role="cell">
              <span className={`member-role ${member.role}`}>
                {roleLabels[member.role]}
              </span>
              <small className="member-permission">
                {member.github_permission || "本地项目权限"}
              </small>
            </div>
            <span
              className={`member-telegram ${member.telegram_id ? "linked" : ""}`}
              role="cell"
            >
              <span
                className={`tiny-dot ${member.telegram_id ? "green" : "gray"}`}
              />
              {member.telegram_id ? "已关联" : "待本人关联"}
            </span>
            {editing === member.id ? (
              <div className="member-classification-editor" role="cell">
                <strong>设置 {member.name} 的分类（可多选）</strong>
                <div className="discipline-selection">
                  {disciplines.map((d) => (
                    <Checkbox
                      key={d}
                      label={disciplineNames[d]}
                      checked={selected.includes(d)}
                      disabled={savingClass}
                      onChange={(e) =>
                        setSelected(
                          e.currentTarget.checked
                            ? [...selected, d]
                            : selected.filter((v) => v !== d),
                        )
                      }
                    />
                  ))}
                </div>
                <p>分类用于任务流转和测试通知。项目访问权限仍跟随 GitHub。</p>
                {classError ? (
                  <p className="inline-error" role="alert">
                    {classError}
                  </p>
                ) : null}
                <div className="discipline-actions">
                  <UiButton
                    className="button primary"
                    disabled={savingClass}
                    onClick={() =>
                      void (async () => {
                        setSavingClass(true);
                        setClassError("");
                        try {
                          await onClassify(member.id, selected);
                          setEditing(null);
                        } catch (error) {
                          setClassError(
                            error instanceof Error
                              ? error.message
                              : "保存失败，请重试",
                          );
                        } finally {
                          setSavingClass(false);
                        }
                      })()
                    }
                  >
                    保存分类
                  </UiButton>
                  <UiButton
                    className="button secondary"
                    disabled={savingClass}
                    onClick={() => setEditing(null)}
                  >
                    取消
                  </UiButton>
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {!matching.length ? (
        <div className="empty-state">
          <Users size={26} />
          <strong>没有匹配的成员</strong>
          <p>调整姓名、分类或团队筛选后重试。</p>
        </div>
      ) : null}
      <p className="member-help">
        开发 / UI / 测试可同时选择。接收私聊通知前，成员需要关联
        Telegram，并在机器人私聊中点击“开始”。
      </p>
      <p className="member-help">
        {project.members_sync_enabled
          ? "GitHub 仓库访问权限决定项目权限。尚未关联 Telegram 的成员可以先分配任务；本人登录 Telegram 并连接同一个 GitHub 账号后，任务和成员记录会自动合并。"
          : "项目管理员可以邀请成员加入。绑定 GitHub 仓库后，成员与权限将自动跟随仓库同步。"}
      </p>
    </section>
  );
}

function SettingsView({
  project,
  boot,
  jobs,
  busy,
  refresh,
  notify,
  fail,
  loadRepos,
  manageProjects,
  simulate,
}: {
  project: Project;
  boot: Bootstrap;
  jobs: ProjectData["jobs"];
  busy: boolean;
  refresh: () => Promise<void>;
  notify: (s: string) => void;
  fail: (e: unknown) => void;
  loadRepos: () => Promise<void>;
  manageProjects: () => void;
  simulate: (s: string) => Promise<void>;
}) {
  const [code, setCode] = useState("");
  const [saving, setSaving] = useState(false);
  const [zone, setZone] = useState(project.timezone);
  const [reminders, setReminders] = useState(project.reminders);
  const [digest, setDigest] = useState(project.daily_digest);
  const [groupUpdates, setGroupUpdates] = useState(project.group_updates);
  const [directNotifications, setDirectNotifications] = useState(
    project.direct_notifications,
  );
  const [autoTesting, setAutoTesting] = useState(project.auto_testing);
  useEffect(() => {
    setZone(project.timezone);
    setReminders(project.reminders);
    setDigest(project.daily_digest);
    setGroupUpdates(project.group_updates);
    setDirectNotifications(project.direct_notifications);
    setAutoTesting(project.auto_testing);
    setCode("");
  }, [
    project.id,
    project.timezone,
    project.reminders,
    project.daily_digest,
    project.group_updates,
    project.direct_notifications,
    project.auto_testing,
  ]);
  const run = async (fn: () => Promise<void>) => {
    setSaving(true);
    try {
      await fn();
    } catch (e) {
      fail(e);
    } finally {
      setSaving(false);
    }
  };
  const owner = project.role === "owner";
  const githubAccountConnected = Boolean(boot.user.github_login);
  const repositoryConnected = project.connection_status === "connected";
  let githubHint = "首次同步会将项目访问权限切换为仓库实际成员权限。";
  if (project.members_sync_enabled) {
    githubHint = `成员自动同步已启用 · ${project.members_synced_at ? `最近同步 ${relative(project.members_synced_at)}` : "等待首次同步"}。在“团队成员”查看账号关联、团队和权限。`;
  } else if (!repositoryConnected) {
    githubHint =
      boot.mode === "demo"
        ? "可选择示例仓库绑定当前项目，或体验项目与成员自动同步。"
        : githubAccountConnected
          ? "账号已连接。下一步安装 GitHub App 并授权仓库，授权的仓库会自动出现在“我的项目”中；也可以选择仓库绑定当前项目。"
          : "先连接个人账号，再安装 GitHub App 并授权需要同步的仓库。";
  }
  return (
    <div className="settings-grid">
      <section className="settings-section">
        <div className="integration-head">
          <span className="integration-logo github">
            <Github size={25} />
          </span>
          <div>
            <h2>仓库与成员</h2>
            <p>当前项目的仓库绑定与成员同步</p>
          </div>
          <span
            className={`connection-badge ${repositoryConnected ? "connected" : ""}`}
          >
            {repositoryConnected ? "仓库已连接" : "未绑定仓库"}
          </span>
        </div>
        <div className="settings-content">
          <div className="setting-row">
            <div>
              <strong>GitHub 来源管理</strong>
              <span>在“项目”统一管理账号授权和仓库来源。</span>
            </div>
            <UiButton className="text-button" onClick={manageProjects}>
              前往项目
              <ChevronRight size={14} />
            </UiButton>
          </div>
          <div className="setting-row">
            <div>
              <strong>项目仓库</strong>
              <span>
                {project.repo_name
                  ? `${project.repo_owner}/${project.repo_name} · ${repositoryConnected ? "已绑定" : "已断开"}`
                  : "当前项目尚未绑定 GitHub 仓库"}
              </span>
            </div>
            {owner ? (
              <UiButton
                className="button secondary"
                disabled={
                  busy || (!boot.githubConfigured && boot.mode !== "demo")
                }
                onClick={() => void loadRepos()}
              >
                {project.repo_name ? "查看仓库" : "选择仓库"}
              </UiButton>
            ) : null}
          </div>
          {boot.githubInstallUrl ? (
            <a
              className="inline-link"
              href={boot.githubInstallUrl}
              target="_blank"
              rel="noreferrer"
            >
              {repositoryConnected
                ? "调整 GitHub App 仓库授权"
                : "安装 GitHub App 并授权仓库"}
              <ExternalLink size={13} />
            </a>
          ) : boot.mode === "live" ? (
            <p className="settings-hint">
              GitHub 接入尚未启用，请联系部署管理员完成配置。
            </p>
          ) : null}
          <p className="settings-hint">{githubHint}</p>
          {project.sync_error ? (
            <p className="sync-warning" role="alert">
              {project.sync_error}
            </p>
          ) : null}
          {project.team_sync_warning ? (
            <p className="sync-warning">{project.team_sync_warning}</p>
          ) : null}
          <div className="integration-footer">
            <span>
              <span
                className={`tiny-dot ${repositoryConnected ? "green" : "gray"}`}
              />
              {boot.mode === "demo"
                ? "当前连接为本地演示，不会访问真实仓库"
                : repositoryConnected
                  ? "Webhook 自动更新，另有每小时对账"
                  : "绑定仓库后，Issue、PR 和成员将自动同步"}
            </span>
            {repositoryConnected && owner ? (
              <UiButton
                className="text-button"
                disabled={saving}
                onClick={() =>
                  void run(async () => {
                    await post(`/api/projects/${project.id}/sync`);
                    await refresh();
                    notify(
                      boot.mode === "demo"
                        ? "演示数据已就绪"
                        : "仓库同步已加入队列",
                    );
                  })
                }
              >
                <RefreshCw size={13} />
                同步
              </UiButton>
            ) : null}
          </div>
        </div>
      </section>
      <section className="settings-section">
        <div className="integration-head">
          <span className="integration-logo telegram">
            <Send size={24} />
          </span>
          <div>
            <h2>Telegram</h2>
            <p>一个群管理多个项目，按项目接收通知</p>
          </div>
          <span
            className={`connection-badge ${project.chat_id ? "connected" : ""}`}
          >
            {project.chat_id
              ? boot.mode === "demo"
                ? "演示连接"
                : "已连接"
              : "未连接"}
          </span>
        </div>
        <div className="settings-content">
          <div className="setting-row">
            <div>
              <strong>项目群</strong>
              <span>
                {project.chat_title || "由群管理员将机器人加入项目群"}
              </span>
            </div>
            {owner ? (
              <UiButton
                className="button secondary"
                disabled={saving}
                onClick={() =>
                  void run(async () => {
                    const result = await post(
                      `/api/projects/${project.id}/link-code`,
                      { kind: "group" },
                    );
                    setCode(`/bind ${result.code}`);
                  })
                }
              >
                生成绑定码
              </UiButton>
            ) : null}
          </div>
          {code ? (
            <div className="bind-instructions">
              <p>
                将机器人加入群后，由生成绑定码的管理员发送以下命令。10
                分钟内有效。
              </p>
              <div className="copy-field">
                <input aria-label="Telegram 群绑定命令" readOnly value={code} />
                <UiButton
                  className="icon-button"
                  aria-label="复制绑定命令"
                  onClick={() =>
                    void navigator.clipboard
                      .writeText(code)
                      .then(() => notify("绑定命令已复制"))
                      .catch(fail)
                  }
                >
                  <Copy size={16} />
                </UiButton>
              </div>
            </div>
          ) : null}
          {owner && project.chat_id ? (
            <div className="setting-row">
              <div>
                <strong>解除当前项目的群绑定</strong>
                <span>停止此项目的群通知，群内其他项目继续使用。</span>
              </div>
              <UiButton
                className="button secondary"
                disabled={saving}
                onClick={() =>
                  void run(async () => {
                    await api(`/api/projects/${project.id}/telegram-group`, {
                      method: "DELETE",
                      body: JSON.stringify({ chat_id: project.chat_id }),
                    });
                    setCode("");
                    await refresh();
                    notify("已解除当前项目的群绑定");
                  })
                }
              >
                解除绑定
              </UiButton>
            </div>
          ) : null}
          <p className="group-project-help">
            多个项目可将绑定码发送到同一个群。每位成员通过 /projects
            选择自己的当前项目，选择互不影响；只有一个可访问项目时自动使用。
          </p>
          <div className="bot-command-list">
            <span>
              <code>/issue</code> AI 整理并确认提交 Issue
            </span>
            <span>
              <code>/cancel</code> 取消 Issue 草稿
            </span>
            <span>
              <code>/projects</code> 选择项目
            </span>
            <span>
              <code>/task</code> 创建任务
            </span>
            <span>
              <code>/tasks</code> 查看进展
            </span>
            <span>
              <code>/tasks all</code> 群内项目总览
            </span>
          </div>
          <div className="integration-footer">
            <span>
              <span
                className={`tiny-dot ${project.chat_id ? "green" : "gray"}`}
              />
              {boot.mode === "demo"
                ? "演示通知仅在本地展示"
                : "绑定码校验项目和群管理员身份"}
            </span>
          </div>
        </div>
      </section>
      <section className="settings-section notification-settings">
        <div className="section-head">
          <h2>
            <Bell size={18} />
            通知与流转
          </h2>
          <span>按项目配置</span>
        </div>
        <div className="settings-content">
          <div className="setting-row">
            <span>
              <strong>群内 Issue / PR 动态</strong>
              <span>新建、变更和代码动态推送到群；默认关闭</span>
            </span>
            <Switch
              size="md"
              aria-label="群内 Issue / PR 动态"
              disabled={!owner}
              checked={groupUpdates}
              onChange={(e) => setGroupUpdates(e.currentTarget.checked)}
            />
          </div>
          <div className="setting-row">
            <span>
              <strong>负责人私聊通知</strong>
              <span>
                任务通知给负责人，待测试通知给测试人员；到期时也会私聊提醒
              </span>
            </span>
            <Switch
              size="md"
              aria-label="负责人私聊通知"
              disabled={!owner}
              checked={directNotifications}
              onChange={(e) => setDirectNotifications(e.currentTarget.checked)}
            />
          </div>
          <div className="setting-row">
            <span>
              <strong>PR 合并后进入测试</strong>
              <span>
                保留开发负责人，优先通知指定测试人员，否则通知项目测试成员
              </span>
            </span>
            <Switch
              size="md"
              aria-label="PR 合并后进入测试"
              disabled={!owner}
              checked={autoTesting}
              onChange={(e) => setAutoTesting(e.currentTarget.checked)}
            />
          </div>
          <div className="setting-row">
            <span>
              <strong>群内到期提醒</strong>
              <span>每天项目当地时间 09:00 汇总到期和逾期任务</span>
            </span>
            <Switch
              size="md"
              aria-label="群内到期提醒"
              disabled={!owner}
              checked={reminders}
              onChange={(e) => setReminders(e.target.checked)}
            />
          </div>
          <div className="setting-row">
            <span>
              <strong>每日项目摘要</strong>
              <span>向项目群发送未完成任务概况</span>
            </span>
            <Switch
              size="md"
              aria-label="每日项目摘要"
              disabled={!owner}
              checked={digest}
              onChange={(e) => setDigest(e.target.checked)}
            />
          </div>
          <div className="setting-row timezone-setting">
            <label htmlFor="timezone">
              <strong>项目时区</strong>
              <span>提醒以项目所在时区为准</span>
            </label>
            <NativeSelect
              id="timezone"
              disabled={!owner}
              value={zone}
              onChange={(e) => setZone(e.target.value)}
            >
              {[
                ...new Set([
                  project.timezone,
                  "America/New_York",
                  "Asia/Shanghai",
                  "Asia/Singapore",
                  "Europe/London",
                  "UTC",
                ]),
              ].map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </NativeSelect>
          </div>
          {owner ? (
            <div className="settings-save">
              <UiButton
                className="button primary"
                disabled={saving}
                onClick={() =>
                  void run(async () => {
                    await patch(`/api/projects/${project.id}/settings`, {
                      timezone: zone,
                      reminders,
                      daily_digest: digest,
                      group_updates: groupUpdates,
                      direct_notifications: directNotifications,
                      auto_testing: autoTesting,
                    });
                    await refresh();
                    notify("通知与流转设置已保存");
                  })
                }
              >
                <Check size={15} />
                保存设置
              </UiButton>
            </div>
          ) : null}
        </div>
      </section>
      {boot.mode === "demo" ? (
        <section className="settings-section demo-events">
          <div className="section-head">
            <h2>
              <Zap size={18} />
              体验 GitHub 联动
            </h2>
            <span>本地模拟</span>
          </div>
          <div className="settings-content">
            <p>模拟一次仓库事件，查看关联 PR 和项目动态如何变化。</p>
            <div className="simulation-buttons">
              <UiButton
                className="button secondary"
                disabled={saving}
                onClick={() => void run(() => simulate("pr_opened"))}
              >
                <GitPullRequest size={16} />
                模拟提交 PR
              </UiButton>
              <UiButton
                className="button secondary"
                disabled={saving}
                onClick={() => void run(() => simulate("pr_merged"))}
              >
                <GitMerge size={16} />
                模拟合并 PR
              </UiButton>
              <UiButton
                className="button secondary"
                disabled={saving}
                onClick={() => void run(() => simulate("ci_failed"))}
              >
                <AlertCircle size={16} />
                模拟 CI 失败
              </UiButton>
            </div>
          </div>
        </section>
      ) : null}
      {jobs.length ? (
        <section className="settings-section queue-section">
          <div className="section-head">
            <h2>同步任务</h2>
            <span>{jobs.length} 项待处理</span>
          </div>
          <div className="settings-content">
            {jobs.map((job) => (
              <div className="setting-row" key={job.id}>
                <div>
                  <strong>
                    {(
                      {
                        github_write: "更新 GitHub Issue",
                        github_import: "导入仓库",
                        github_installation_sync: "同步仓库项目",
                        github_project_sync: "同步成员与权限",
                        telegram_notice: "分发任务通知",
                        telegram_delivery: "发送通知",
                      } as Record<string, string>
                    )[job.kind] || "同步事件"}
                  </strong>
                  <span>
                    {job.last_error ||
                      (job.status === "processing" ? "处理中" : "等待处理")}
                  </span>
                </div>
                {job.status === "failed" && owner ? (
                  <UiButton
                    className="button secondary"
                    disabled={saving}
                    onClick={() =>
                      void run(async () => {
                        await post(
                          `/api/projects/${project.id}/jobs/${job.id}/retry`,
                        );
                        await refresh();
                        notify("已重新加入队列");
                      })
                    }
                  >
                    重试
                  </UiButton>
                ) : (
                  <span className="muted">{job.attempts}/5</span>
                )}
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function ProjectModal({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (name: string, description: string) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <Overlay title="新建项目" onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            await onCreate(name, description);
          } catch (e) {
            setError((e as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <div className="dialog-content">
          <TextInput
            className="ui-form-field"
            label="项目名称"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={80}
            placeholder="例如：产品研发"
          />
          <Textarea
            className="ui-form-field"
            label="项目描述"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="这个项目想完成什么？"
          />
          {error ? <p className="form-error">{error}</p> : null}
        </div>
        <div className="dialog-footer">
          <UiButton
            type="button"
            className="button secondary"
            onClick={onClose}
          >
            取消
          </UiButton>
          <UiButton type="submit" className="button primary" disabled={busy}>
            创建项目
          </UiButton>
        </div>
      </form>
    </Overlay>
  );
}
function RepoModal({
  project,
  repos,
  onClose,
  onBind,
}: {
  project: Project;
  repos: RepositoryChoice[];
  onClose: () => void;
  onBind: (repo: RepositoryChoice) => Promise<void>;
}) {
  const [chosen, setChosen] = useState(project.repo_id || repos[0]?.id || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <Overlay title="选择 GitHub 仓库" onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          const repo = repos.find((r) => r.id === chosen);
          if (!repo) return;
          setBusy(true);
          try {
            await onBind(repo);
          } catch (e) {
            setError((e as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <div className="dialog-content">
          <p className="muted">
            选择已授权给 TGFlow 的仓库，连接到「{project.name}」。
          </p>
          <p className="settings-hint">
            这里为当前项目绑定一个仓库。要同时管理多个仓库，请在“管理项目”中按归属批量添加；同步会为每个仓库分别创建项目。
          </p>
          {repos.length ? (
            repos.map((repo) => (
              <label
                className={`repo-option ${chosen === repo.id ? "selected" : ""}`}
                key={repo.id}
              >
                <input
                  type="radio"
                  name="repo"
                  value={repo.id}
                  checked={chosen === repo.id}
                  onChange={() => setChosen(repo.id)}
                />
                <Github size={19} />
                <strong>
                  {repo.owner}/{repo.name}
                </strong>
                <span>{repo.private ? "私有" : "公开"}</span>
              </label>
            ))
          ) : (
            <p>没有可用仓库。请先安装 GitHub App 并授权仓库，再重新选择。</p>
          )}
          {error ? <p className="form-error">{error}</p> : null}
        </div>
        <div className="dialog-footer">
          <UiButton
            type="button"
            className="button secondary"
            onClick={onClose}
          >
            取消
          </UiButton>
          <UiButton
            type="submit"
            className="button primary"
            disabled={!chosen || busy}
          >
            绑定仓库
          </UiButton>
        </div>
      </form>
    </Overlay>
  );
}
